import * as css from './VideoCard.css';
import Pencil from '../../../assets/icons/Pencil';
import Video from '../../../assets/icons/Video';
import PermissionBanner from '../PermissionBanner/PermissionBanner';
import {TSupportVideos} from '../../../redux/management/management.type';
import {useTranslation} from 'react-i18next';
import MasterPermission from '../../../pages/components/rolePermission/MasterPermission';
import ArrowLine from '../../../assets/icons/ArrowLine';
import {Button} from '@tickchak/react-share/app';
import {trackEvent} from '../../../utils/analytics';

interface VideoCardProps {
    video: TSupportVideos;
    editVideo: (id: number) => void;
    onOpenVideo: (video: TSupportVideos) => void;
    currentExpended: number;
    setCurrentExpended: (id: number) => void;
}

function VideoCard({video, editVideo, onOpenVideo, currentExpended, setCurrentExpended}: VideoCardProps) {
    const {t} = useTranslation('layout');
    
    const expended = currentExpended === video.id;
    const setExpended = (newExpended: boolean) => {
        if (newExpended) {
            setCurrentExpended(video.id);
            // Send Google Analytics event for accordion expansion
            trackEvent('video_card_expanded', {
                event_category: 'support_videos',
                video_id: video.id,
                video_title: video.title,
                video_page: video.page,
                event_label: 'video id: ' + video.id
            });
        } else {
            setCurrentExpended(-1);
        }
    };

    const handleWatchVideo = (e: React.MouseEvent) => {
        e.stopPropagation();
        onOpenVideo(video);
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        editVideo(video.id);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpended(!expended);
        }
    };

    const contentId = `video-content-${video.id}`;

    return (
        <css.video 
            className="video-card"
            role="button"
            tabIndex={0}
            aria-expanded={expended}
            aria-controls={contentId}
            $hide={Boolean(video.hide)} 
            $expended={expended} 
            onClick={() => setExpended(!expended)} 
            onKeyDown={handleKeyDown}
        >            
            <css.videoTitle $expended={expended}>
                <Video width={20} height={20} />
                {video.title}
                <css.arrowIcon $expended={expended}>
                    <ArrowLine />
                </css.arrowIcon>
            </css.videoTitle>
            
            
            <MasterPermission>
                <css.editButtonWrapper>
                    <Button 
                        variant="brand"
                        size="category"
                        iconStart={<Pencil />}
                        onClick={handleEdit}
                        title={t('edit')}
                    />
                </css.editButtonWrapper>
            </MasterPermission>
            
            <css.videoContent id={contentId} $expended={expended}> 
                <css.descriptionText>{video.description}</css.descriptionText>
                <PermissionBanner permissions={video.permission} />
                <css.actionButtons>
                    <Button 
                        variant="brand"
                        size="category"
                        onClick={handleWatchVideo}
                        data-cy="watch-video-btn"
                        iconStart={<Video />}
                    >
                        {t('watchVideo')}
                    </Button>
                    <Button 
                        variant="primary"
                        size="category"
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpended(false);
                        }}
                        data-cy="close-accordion-btn"
                    >
                        {t('closeIt')}
                    </Button>
                </css.actionButtons>
            </css.videoContent>
        </css.video>
    );
}

export default VideoCard;
